import { useEffect, useMemo, useRef, useState, type SyntheticEvent } from "react";
import ExpandedVideoModal from "../components/ExpandedVideoModal";
import PageSEO from "../components/PageSEO";
import SiteFooter from "../components/SiteFooter";
import SiteHeader from "../components/SiteHeader";
import ActionPanel from "../sections/ActionPanel";
import ApiSnippet from "../sections/ApiSnippet";
import Features from "../sections/Features";
import FinalCta from "../sections/FinalCta";
import FlowTheater from "../sections/FlowTheater";
import Hero from "../sections/Hero";
import HowItWorks from "../sections/HowItWorks";
import LogoStrip from "../sections/LogoStrip";
import MultiAgentDiagram from "../sections/MultiAgentDiagram";
import Timeline from "../sections/Timeline";
import WhatIsAnterPassage from "../sections/WhatIsAnterPassage";
import WhyAnter from "../sections/WhyAnter";
import WhyNotThem from "../sections/WhyNotThem";
import EmbedBadgeSection from "../components/EmbedBadgeSection";
// The video, its chapters, and its clock live in one module shared with the
// watch page — the two pages play the same recording and must not drift.
import { FLOW_EVENTS, VIDEO_WATCH_PATH, formatClock } from "../content/demoVideo";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "SoftwareApplication",
      "@id": "https://anter.ai/#software",
      name: "Anter",
      description:
        "Anter is a managed agents platform for building and orchestrating multi-agent AI systems, then shipping them as streaming APIs. Visual builder, tool routing, multi-LLM support, and full observability — production-ready, without building the infrastructure yourself.",
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Web",
      url: "https://anter.ai",
      keywords:
        "managed agents, managed AI agents, managed agents platform, multi-agent orchestration, AI agent orchestration platform, MCP tools, multi-LLM agents",
      featureList: [
        "Managed agent sessions — hosted execution, streaming, and state, across any LLM provider",
        "Multi-agent orchestration with transfer and delegation routing",
        "White-label agent-management console and embeddable chat SDK",
        "MCP server and HTTP tool integration",
        "Triage-first ReAct execution engine",
        "SSE streaming with replay and durability",
        "OpenTelemetry observability and execution tracing",
        "Multi-LLM provider routing (OpenAI, Anthropic, Azure, Google)",
        "A2A protocol support for agent interoperability",
      ],
      publisher: {
        "@id": "https://anter.ai/#organization",
      },
    },
    {
      "@type": "Organization",
      "@id": "https://anter.ai/#organization",
      name: "Anter",
      // "anter" collides with unrelated entities in search (antern, anternet,
      // …), which is why the brand query ranks off page one. `alternateName`
      // plus a `description` are what Google uses to resolve us as a distinct
      // entity — keep both specific and keep them in sync with the WebSite node.
      alternateName: ["Anter AI", "anter.ai"],
      description:
        "Anter is a managed agents platform for building, orchestrating, and shipping multi-agent AI systems as streaming production APIs.",
      url: "https://anter.ai",
      logo: "https://anter.ai/favicon.svg",
      email: "hello@anter.ai",
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: "hello@anter.ai",
        url: "https://anter.ai/pricing",
        availableLanguage: "English",
      },
      sameAs: [
        "https://github.com/anter-ai",
        "https://x.com/meetanter",
        "https://www.linkedin.com/company/anterai",
      ],
    },
    {
      "@type": "WebSite",
      "@id": "https://anter.ai/#website",
      url: "https://anter.ai",
      name: "Anter",
      alternateName: ["Anter AI", "anter.ai"],
      description: "Build and orchestrate managed AI agents, then ship them as streaming APIs.",
      inLanguage: "en-US",
      publisher: {
        "@id": "https://anter.ai/#organization",
      },
    },
    {
      "@type": "FAQPage",
      "@id": "https://anter.ai/#faq",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is Anter?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Anter is an enterprise-grade managed AI agents platform and runtime system for building, orchestrating, and shipping autonomous multi-agent systems as production streaming APIs. It combines visual agent building with deterministic routing, multi-LLM support (OpenAI, Anthropic, Azure, Google), MCP tool routing, and end-to-end execution tracing without requiring teams to build custom hosting infrastructure.",
          },
        },
        {
          "@type": "Question",
          name: "What are managed agents?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Managed agents are AI agents that run as a hosted, streaming session instead of infrastructure you build and operate yourself — the model, tools, and orchestration logic are defined once, then executed with built-in state, tool calls, and observability. Anter is a managed agents platform that works across any LLM provider (OpenAI, Anthropic, Azure, Google), with multi-agent orchestration, MCP tool routing, and full execution tracing built in — so teams ship managed agents as production APIs without building the hosting layer themselves.",
          },
        },
      ],
    },
  ],
};

export default function Home() {
  const [currentSecond, setCurrentSecond] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const activeEventIndex = useMemo(() => {
    let index = 0;
    for (let i = 0; i < FLOW_EVENTS.length; i += 1) {
      const event = FLOW_EVENTS[i];
      if (event && currentSecond >= event.second) index = i;
    }
    return index;
  }, [currentSecond]);

  const activeTag = FLOW_EVENTS[activeEventIndex]?.tag ?? "Tool Call";

  function seekTo(second: number) {
    setCurrentSecond(second);
  }

  function openExpanded() {
    setExpanded(true);
  }

  function openLiveAgent() {
    window.dispatchEvent(new CustomEvent("anter:open-chat"));
  }

  useEffect(() => {
    if (!expanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [expanded]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="Anter — Managed AI Agents Platform & Multi-Agent Orchestration"
        description="Build, orchestrate, and ship managed AI agents as production streaming APIs. Visual builder, deterministic routing, MCP tools, multi-LLM support, and execution tracing."
        canonical="/"
        jsonLd={JSON_LD}
      />

      <div className="relative min-h-dvh overflow-x-hidden bg-bg font-sans text-text transition-colors duration-300">
        {/* Ambient background */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 15%, rgba(16,185,129,0.14), transparent 33%), radial-gradient(circle at 80% 30%, rgba(59,130,246,0.12), transparent 38%), linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "auto, auto, 40px 40px, 40px 40px",
          }}
        />

        <SiteHeader scrolled={scrolled} />

        <main id="main-content" tabIndex={-1}>
          {/* Hero — constrained */}
          <div className="mx-auto w-full max-w-7xl px-4 sm:px-5 md:px-8">
            <Hero />
          </div>

          {/* LogoStrip — full bleed */}
          <LogoStrip />

          {/* All remaining sections — constrained */}
          <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 sm:px-5 md:px-8">
            <WhatIsAnterPassage />
            <WhyAnter />
            <WhyNotThem />
            <Features />
            <MultiAgentDiagram />
            <HowItWorks />

            {/* Demo section */}
            <section id="demo" className="py-20">
              <div className="mb-10">
                <p className="mb-3 text-xs font-semibold uppercase tracking-[0.16em] text-primary-text">
                  Live walkthrough
                </p>
                <h2 className="max-w-2xl text-3xl font-extrabold tracking-tight text-text sm:text-4xl">
                  See Anter running
                  <br />
                  real workflows.
                </h2>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-text-muted">
                  A real recording of the product — no mock screenshots. Watch a managed agent
                  handle orchestration, routing, tool calls, and observability, live.
                </p>
                {/* Internal link to the video's watch page. This is how the
                    watch page gets discovered and weighted — it is reachable
                    from no nav menu. */}
                <a
                  href={VIDEO_WATCH_PATH}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-text transition hover:opacity-80"
                >
                  Open the full demo with chapters
                  <span aria-hidden>→</span>
                </a>
              </div>

              <FlowTheater
                activeTag={activeTag}
                currentSecond={currentSecond}
                formatClock={formatClock}
                onExpand={openExpanded}
                startSecond={currentSecond}
              />

              <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
                <Timeline
                  events={FLOW_EVENTS}
                  activeEventIndex={activeEventIndex}
                  formatClock={formatClock}
                  onSeekTo={seekTo}
                />
                <ActionPanel onOpenLiveAgent={openLiveAgent} />
              </div>
            </section>

            <ApiSnippet />
            <EmbedBadgeSection />
            <FinalCta />
          </div>
        </main>

        <SiteFooter />

        <ExpandedVideoModal
          open={expanded}
          onClose={() => setExpanded(false)}
          startSecond={currentSecond}
        />
      </div>
    </>
  );
}
